import React, { FC } from 'react';
import { Platform, Pressable, StyleSheet, View, type ViewStyle } from 'react-native';
import { TabBar as RNTabBar, TabBarItem, type TabBarItemProps } from 'react-native-tab-view';

import { Center, Text } from '@fhs-legacy/universal-components';
import { theme } from 'styles/configure-theme';

import { ColorOptions, ITab, ITabBarProps } from './types';

/** Route fields used for tab labels (structurally compatible with ITab / react-native-tab-view Route). */
type TabLabelRoute = { title?: string; subTitle?: string; disabled?: boolean };

const getIndicatorColor = (color?: ColorOptions) => {
  return color === 'inverted' ? Styles.color.white : theme.token('background-active');
};

const getTextColor = (color?: ColorOptions, disabled?: boolean) => {
  if (disabled) {
    return theme.token(color === 'inverted' ? 'text-disabled-reversed' : 'text-disabled');
  } else if (color === 'inverted') {
    return Styles.color.white;
  } else if (color === 'secondary') {
    return Styles.color.accent;
  }
  return theme.token('text-default');
};

const renderFhsTabLabel = (route: TabLabelRoute, focused: boolean, color?: ColorOptions) => (
  <Center>
    <Text
      variant="copyOne"
      margin={0}
      minWidth="180px"
      textAlign="center"
      color={focused ? theme.token('background-active') : getTextColor(color, route.disabled)}
      fontWeight={focused ? '700' : '400'}
    >
      {route.title}
    </Text>
    {route.subTitle ? (
      <Text variant="copyOne" color={getTextColor(color, route.disabled)}>
        {route.subTitle}
      </Text>
    ) : null}
  </Center>
);

const webTabStyles = StyleSheet.create({
  pressable: {
    backgroundColor: 'transparent',
  },
  item: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
    minHeight: 48,
  },
});

/**
 * Web: single label tree + no aria-label so accessible name matches visible text (Lighthouse).
 * Native: still uses TabBarItem with stacked label animation.
 */
const FhsWebTabBarItem: FC<{
  itemProps: TabBarItemProps<ITab>;
  color?: ColorOptions;
}> = ({ itemProps, color }) => {
  const { onPress, onLongPress, onLayout, route, navigationState, style, defaultTabWidth, testID, accessible } =
    itemProps;
  // Use route key, not reference equality — avoids wrong index when routes are recreated each render.
  const tabIndex = navigationState.routes.findIndex(r => r.key === route.key);
  const isFocused = tabIndex === navigationState.index;
  const flat = StyleSheet.flatten(style);
  const isWidthSet = flat?.width !== undefined;
  const tabContainerStyle: ViewStyle | null = isWidthSet ? null : { width: defaultTabWidth };

  const webTabA11y =
    Platform.OS === 'web'
      ? ({
          // Match react-native-tab-view TabBarItem: explicit DOM role/selection so SR does not treat every tab as selected.
          role: 'tab',
          'aria-selected': isFocused,
        } as Record<string, unknown>)
      : {
          accessibilityRole: 'tab' as const,
          accessibilityState: { selected: isFocused },
        };

  return (
    <Pressable
      {...webTabA11y}
      accessibilityLabel={route.title}
      accessible={accessible}
      testID={testID}
      onLayout={onLayout}
      onPress={onPress}
      onLongPress={onLongPress}
      style={[webTabStyles.pressable, tabContainerStyle]}
    >
      <View
        accessible={false}
        importantForAccessibility="no-hide-descendants"
        pointerEvents="none"
        style={[webTabStyles.item, style]}
      >
        {renderFhsTabLabel(route, isFocused, color)}
      </View>
    </Pressable>
  );
};

const TabBar: FC<React.PropsWithChildren<ITabBarProps>> = ({
  color,
  roundTopCorners,
  tabBarBackgroundColor,
  ...props
}) => {
  return (
    <RNTabBar
      {...props}
      onTabPress={ev => {
        if (ev.route.disabled) {
          ev.preventDefault();
        }
      }}
      pressColor="transparent"
      style={[
        roundTopCorners && {
          borderTopStartRadius: 30,
          borderTopEndRadius: 30,
        },
        {
          backgroundColor: tabBarBackgroundColor,
        },
      ]}
      indicatorContainerStyle={{
        borderBottomWidth: 1,
        borderBottomColor: theme.token('border-color-default'),
      }}
      indicatorStyle={{ backgroundColor: getIndicatorColor(color), height: 3 }}
      renderTabBarItem={tabBarItemProps => {
        const { key, ...rest } = tabBarItemProps as TabBarItemProps<ITab> & { key: string };
        if (Platform.OS === 'web') {
          return <FhsWebTabBarItem key={key} itemProps={rest} color={color} />;
        }
        return (
          <TabBarItem key={key} {...rest} label={({ route, focused }) => renderFhsTabLabel(route, focused, color)} />
        );
      }}
    />
  );
};

export default TabBar;
